import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Import ikon pendukung gaya tampilan baru
import { Store, Users, Package, DollarSign, Layers, TrendingUp, X, Search } from 'lucide-react';

export default function ProductListModal({ isOpen, onClose, activeDept, dbData = [] }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('rating'); // 'rating' | 'contributor'

  // Reset state saat modal dibuka/tutup
  useEffect(() => {
    if (!isOpen) {
      setSelectedProduct(null);
      setSearchQuery('');
      setActiveTab('rating');
    }
  }, [isOpen]);

  // 1. TRANSFORMASI DATA & PENYUNTIKAN DUMMY ANALISA GUDANG (SSR)
  const processedProducts = useMemo(() => {
    let actualData = [];
    if (Array.isArray(dbData)) {
      actualData = dbData;
    } else if (dbData && Array.isArray(dbData.data)) {
      actualData = dbData.data;
    } else if (dbData && typeof dbData === 'object') {
      actualData = dbData.data?.data || [];
    }

    if (actualData.length === 0) return [];

    const productMap = {};

    actualData.forEach((row) => {
      const artikel = row.artikel || row.Artikel || row.ARTIKEL || row.id_artikel || row.sku || row.SKU;
      
      if (!artikel) return;

      if (!productMap[artikel]) {
        const namaProduk = row.nama_produk || row.nama || row.product_name || `Artikel ${artikel}`;
        const gambarProduk = row.gambar || 'https://placehold.co/150x150?text=No+Image';
        const brandProduk = row.brand || row.Brand || activeDept?.name || 'Product Brand';
        
        // --- DUMMY GENERATOR UNTUK LOGISTIK GUDANG & SSR ---
        const charCodeSum = artikel.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const initialStock = (charCodeSum % 180) + 40; 
        const currentStock = Math.max(5, initialStock - ((charCodeSum % 35) + 10)); 

        // --- TEMPLATE DUMMY UNTUK RATING & CUSTOMER REVIEWS ---
        const baseRating = 4.0 + ((charCodeSum % 11) / 10); 
        const ratingCount = (charCodeSum % 45) + 5; 
        
        const customerReviewsTemplate = [
          { name: 'Budi Santoso', rating: 5, date: '02 Jun 2026', comment: 'Kualitas bahan sangat bagus, ukuran pas sekali.' },
          { name: 'Siti Rahma', rating: 4, date: '28 Mei 2026', comment: 'Pengiriman cepat, produk original dan sesuai deskripsi.' },
          { name: 'Andi Wijaya', rating: 5, date: '15 Mei 2026', comment: 'Sangat puas dengan pembelian ini, bakal order lagi nanti.' },
          { name: 'Dewi Lestari', rating: 4, date: '10 Mei 2026', comment: 'Bagus, cuma agak sedikit ketat di bagian lengan.' },
          { name: 'Rian Hidayat', rating: 5, date: '01 Mei 2026', comment: 'Respon penjual ramah dan produknya juara harganya murah.' },
        ].slice(0, Math.min(5, ratingCount));

        productMap[artikel] = {
          id: artikel,
          SKU: artikel,
          name: namaProduk,
          brand: brandProduk,
          sales: 0,
          revenue: 0,
          netRevenue: 0,
          image: gambarProduk,
          ranking: row.ranking ? String(row.ranking) : '-',
          initialStock: initialStock,
          currentStock: currentStock,
          contributorsMap: {},
          rating: Number(baseRating.toFixed(1)),
          ratingCount: ratingCount,
          reviews: customerReviewsTemplate
        };
      }

      const qty = Number(row.total_qty || row.qty || row.quantity) || 0;
      const gross = Number(row.gross_revenue || row.gross) || 0;
      const net = Number(row.net_revenue || row.net) || 0;
      const namaContributor = row.contributor || row.crew || row.nama_crew;

      productMap[artikel].sales += qty;
      productMap[artikel].revenue += gross;
      productMap[artikel].netRevenue += net;
      
      if (namaContributor) {
        if (!productMap[artikel].contributorsMap[namaContributor]) {
          productMap[artikel].contributorsMap[namaContributor] = { name: namaContributor, qty: 0, revenue: 0 };
        }
        productMap[artikel].contributorsMap[namaContributor].qty += qty;
        productMap[artikel].contributorsMap[namaContributor].revenue += net;
      }
    });

    return Object.values(productMap).map(product => {
      const contributors = Object.values(product.contributorsMap).sort((a, b) => b.qty - a.qty);
      const totalSupply = product.currentStock + product.sales;
      const ssr = totalSupply > 0 ? Number(((product.sales / totalSupply) * 100).toFixed(1)) : 0;
      
      let stockHealth = 'HEALTHY';
      let healthColor = 'text-emerald-700 bg-emerald-50 border-emerald-200';
      
      if (ssr > 75 && product.currentStock < 15) {
        stockHealth = 'CRITICAL OVERSELL';
        healthColor = 'text-rose-700 bg-rose-50 border-rose-200';
      } else if (ssr < 20 && product.currentStock > 80) {
        stockHealth = 'DEAD STOCK RISK';
        healthColor = 'text-amber-700 bg-amber-50 border-amber-200';
      } else if (product.currentStock <= 10) {
        stockHealth = 'LOW STOCK WARNING';
        healthColor = 'text-orange-700 bg-orange-50 border-orange-200';
      }

      const dailySalesVelocity = product.sales / 30;
      const daysLeft = dailySalesVelocity > 0 ? Math.ceil(product.currentStock / dailySalesVelocity) : 999;

      return { 
        ...product, 
        contributors, 
        ssr, 
        stockHealth, 
        healthColor,
        daysLeft: daysLeft === 999 ? '∞' : `${daysLeft} Hari`
      };
    }).sort((a, b) => b.sales - a.sales);
  }, [dbData, activeDept]);

  // 2. FILTER PRODUK BERDASARKAN PENCARIAN
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return processedProducts;
    return processedProducts.filter(product => 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      product.id.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [processedProducts, searchQuery]);

  // 3. HITUNG METRIK MACRO DASHBOARD
  const metrics = useMemo(() => {
    if (processedProducts.length === 0) return { totalSKU: 0, avgSsr: 0, totalSales: 0, healthRatio: 0 };
    
    const totalSKU = processedProducts.length;
    const totalSales = processedProducts.reduce((acc, p) => acc + p.sales, 0);
    const sumSsr = processedProducts.reduce((acc, p) => acc + p.ssr, 0);
    const healthySKU = processedProducts.filter(p => p.stockHealth === 'HEALTHY').length;

    return { 
      totalSKU, 
      totalSales, 
      avgSsr: (sumSsr / totalSKU).toFixed(1),
      healthRatio: ((healthySKU / totalSKU) * 100).toFixed(0)
    };
  }, [processedProducts]);

  const fallbackDept = Array.isArray(dbData) 
    ? dbData[0]?.dept 
    : (dbData?.data?.[0]?.dept || dbData?.data?.data?.[0]?.dept);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 md:p-4 overflow-hidden font-sans text-xs selection:bg-indigo-500 selection:text-white">
        
        {/* BACKDROP - backdrop-blur-xl & bg-slate-950/40 sesuai referensi baru */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-950/40 backdrop-blur-xl"
        />

        {/* WINDOW MODAL CONTAINER */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="relative bg-white w-full h-full md:h-[85vh] md:w-[95vw] md:max-w-5xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden z-10 border border-slate-200 text-slate-800"
        >
          
          {/* HEADER MODAL */}
          <div className="bg-slate-50/80 border-b border-slate-100 px-4 py-3.5 flex items-center justify-between shrink-0">
            <div>
              <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                <span className="font-bold tracking-widest text-indigo-600 bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md uppercase">
                  {activeDept?.name || fallbackDept || 'DEPARTMENT'} AUDIT
                </span>
                <span className="text-slate-300">/</span>
                <span className="text-slate-500 font-medium">Logistik & Penjualan Terintegrasi</span>
              </div>
              <h3 className="text-sm font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span>Daftar Inventori & Analitik SSR</span>
              </h3>
            </div>
            
            <button 
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 border border-slate-200/40 transition shadow-xs"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* MAIN GRID BODY */}
          <div className="flex-1 flex overflow-hidden w-full relative flex-col md:flex-row">
            
            {/* ================= PANEL KIRI: LISTING PRODUK ================= */}
            <div className={`h-full flex flex-col p-4 overflow-y-auto transition-all duration-200 w-full bg-slate-50/40 ${
              selectedProduct ? 'md:w-[45%] md:border-r md:border-slate-200' : 'w-full'
            }`}>
              
              {/* Macro Cards Top Matrix Highlights */}
              <div className="grid grid-cols-4 gap-2 mb-3.5 shrink-0 text-[11px]">
                <div className="bg-white border border-slate-200/80 rounded-xl p-2.5 shadow-xs flex flex-col justify-center">
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Total SKU</span>
                  <div className="font-bold text-slate-900 text-sm mt-0.5">{metrics.totalSKU}</div>
                </div>
                <div className="bg-white border border-slate-200/80 rounded-xl p-2.5 shadow-xs flex flex-col justify-center">
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Terjual</span>
                  <div className="font-bold text-indigo-600 text-sm mt-0.5">
                    {metrics.totalSales.toLocaleString('id-ID')} <span className="text-[9px] font-normal text-slate-400">pcs</span>
                  </div>
                </div>
                <div className="bg-white border border-slate-200/80 rounded-xl p-2.5 shadow-xs flex flex-col justify-center">
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Avg SSR</span>
                  <div className="font-bold text-emerald-600 text-sm mt-0.5">{metrics.avgSsr}%</div>
                </div>
                <div className="bg-white border border-slate-200/80 rounded-xl p-2.5 shadow-xs flex flex-col justify-center">
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Health %</span>
                  <div className="font-bold text-slate-900 text-sm mt-0.5">{metrics.healthRatio}%</div>
                </div>
              </div>

              {/* Input Pencarian */}
              {processedProducts.length > 0 && (
                <div className="mb-3.5 relative shrink-0">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                    <Search className="w-3.5 h-3.5" />
                  </span>
                  <input
                    type="text"
                    placeholder="Cari produk atau SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-500 transition-all shadow-xs"
                  />
                </div>
              )}
              
              {/* Product Map List */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-10 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200 flex flex-col items-center justify-center p-4 flex-1">
                  SKU tidak ditemukan.
                </div>
              ) : (
                <div className="flex flex-col gap-2 pr-1 overflow-y-auto flex-1 scrollbar-thin">
                  {filteredProducts.map((product) => {
                    const isSelected = selectedProduct?.id === product.id;
                    return (
                      <div
                        key={product.id}
                        onClick={() => setSelectedProduct(product)}
                        className={`cursor-pointer p-3 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                          isSelected 
                            ? 'bg-white border-indigo-600 ring-4 ring-indigo-50' 
                            : 'bg-white border-slate-200/80 hover:border-slate-300 shadow-xs'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 shrink-0 shadow-xs flex items-center justify-center relative">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              onError={(e) => { e.currentTarget.src = 'https://placehold.co/150x150?text=No+Photo' }} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                              <span className="font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded">#{product.ranking}</span>
                              <span className={`px-1.5 py-0.5 rounded border font-bold ${product.healthColor}`}>
                                SSR {product.ssr}%
                              </span>
                            </div>
                            <h5 className="font-bold text-slate-900 mt-1 truncate tracking-tight">{product.name}</h5>
                            <span className="text-[10px] text-slate-400 block font-mono">SKU: {product.id}</span>
                          </div>
                        </div>

                        <div className="text-right shrink-0 border-l border-slate-100 pl-3 font-mono">
                          <div>
                            <span className="text-slate-900 font-bold">{product.sales}</span> <span className="text-[10px] font-sans text-slate-400">pcs</span>
                          </div>
                          <div className="text-[11px] text-emerald-600 font-bold mt-0.5">Rp{product.netRevenue.toLocaleString('id-ID')}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ================= PANEL KANAN: DEEP-DIVE POPUP MODEL DENGAN TABS ================= */}
            <AnimatePresence>
              {selectedProduct && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.96, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: 10 }}
                  transition={{ type: "spring", duration: 0.3 }}
                  className="absolute inset-0 md:relative md:w-[55%] h-full bg-white flex flex-col p-4 overflow-y-auto z-20 border-t md:border-t-0 border-slate-200"
                >
                  {/* Header Detail Popup */}
                  <div className="p-3 border-b border-slate-100 bg-slate-50/80 rounded-xl flex items-center justify-between mb-4 shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 bg-white shrink-0 shadow-xs flex items-center justify-center">
                        <img 
                          src={selectedProduct.image} 
                          alt="" 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.src = 'https://placehold.co/150x150?text=No+Photo' }} 
                        />
                      </div>
                      <div className="truncate">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-600">
                            {selectedProduct.brand}
                          </span>
                          <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full font-mono border border-slate-200/60">
                            {selectedProduct.id}
                          </span>
                        </div>
                        <h4 className="font-bold text-slate-900 tracking-tight mt-0.5 truncate">{selectedProduct.name}</h4>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedProduct(null)} 
                      className="p-1.5 rounded-lg bg-slate-200/60 text-slate-500 hover:bg-slate-200 transition"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="flex flex-col gap-4 flex-1 overflow-y-auto pr-1">
                    
                    {/* KPI HIGHLIGHTS DETAIL */}
                    <div className="grid grid-cols-3 gap-2 bg-slate-50/60 border border-slate-200/60 p-2.5 rounded-xl text-center text-[11px]">
                      <div className="bg-white border border-slate-200/60 p-2 rounded-lg shadow-2xs">
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Sisa Stok</span>
                        <span className="font-bold text-slate-800 text-xs font-mono mt-0.5 block">
                          {selectedProduct.currentStock} <span className="text-[9px] font-sans font-normal text-slate-500">pcs</span>
                        </span>
                      </div>
                      <div className="bg-white border border-slate-200/60 p-2 rounded-lg shadow-2xs">
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Sell-Through</span>
                        <span className="font-bold text-emerald-600 text-xs font-mono mt-0.5 block">{selectedProduct.ssr}%</span>
                      </div>
                      <div className="bg-white border border-slate-200/60 p-2 rounded-lg shadow-2xs">
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Stock Out</span>
                        <span className="font-bold text-orange-600 text-xs font-mono mt-0.5 block">{selectedProduct.daysLeft}</span>
                      </div>
                    </div>

                    {/* INTERFACE NAVIGASI TAB MODAL */}
                    <div className="flex bg-slate-50/50 p-1.5 border border-slate-200/60 rounded-xl gap-2 shrink-0">
                      <button
                        onClick={() => setActiveTab('rating')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-lg border transition-all duration-200 ${
                          activeTab === 'rating' 
                            ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm' 
                            : 'text-slate-500 hover:text-slate-700 bg-white border-slate-200 hover:border-slate-300 shadow-2xs'
                        }`}
                      >
                        <Users className="w-3.5 h-3.5" />
                        <span>Rating ({selectedProduct.rating})</span>
                      </button>
                      <button
                        onClick={() => setActiveTab('contributor')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-lg border transition-all duration-200 ${
                          activeTab === 'contributor' 
                            ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm' 
                            : 'text-slate-500 hover:text-slate-700 bg-white border-slate-200 hover:border-slate-300 shadow-2xs'
                        }`}
                      >
                        <Store className="w-3.5 h-3.5" />
                        <span>Crew ({selectedProduct.contributors.length})</span>
                      </button>
                    </div>

                    {/* CONTAINER KONTEN DATA TAB LIST */}
                    <div className="flex-1 min-h-[250px]">
                      
                      {/* KONTEN TAB: CUSTOMER RATING */}
                      {activeTab === 'rating' && (
                        <div className="flex flex-col gap-2 animate-fadeIn">
                          <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                            <span>★ {selectedProduct.rating} / 5.0 ({selectedProduct.ratingCount} Ulasan Pelanggan)</span>
                          </div>
                          <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto pr-1">
                            {selectedProduct.reviews.map((review, idx) => (
                              <div key={idx} className="bg-white border border-slate-200/80 p-3 rounded-xl text-[11px] shadow-2xs">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-bold text-slate-800">{review.name}</span>
                                  <span className="text-[9px] text-slate-400 font-mono">{review.date}</span>
                                </div>
                                <div className="flex text-amber-400 text-[10px] mb-1.5">
                                  {[...Array(5)].map((_, i) => (
                                    <span key={i}>{i < review.rating ? '★' : '☆'}</span>
                                  ))}
                                </div>
                                <p className="text-slate-600 italic bg-slate-50 p-2 rounded-lg border border-slate-100">
                                  "{review.comment}"
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* KONTEN TAB: CONTRIBUTOR LEADERBOARD */}
                      {activeTab === 'contributor' && (
                        <div className="flex flex-col gap-2 animate-fadeIn">
                          <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto pr-1">
                            {selectedProduct.contributors.length === 0 ? (
                              <div className="text-slate-400 text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                Tidak ada klaim kru untuk artikel ini.
                              </div>
                            ) : (
                              selectedProduct.contributors.map((c, index) => {
                                const percentage = selectedProduct.netRevenue > 0 ? ((c.revenue / selectedProduct.netRevenue) * 100).toFixed(0) : 0;
                                return (
                                  <div key={index} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] hover:bg-slate-100/50 transition shadow-2xs">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <span className="w-5 h-5 rounded-md bg-slate-900 text-[10px] font-bold text-white flex items-center justify-center shrink-0 shadow-2xs">
                                        {index + 1}
                                      </span>
                                      <div className="min-w-0">
                                        <span className="font-bold text-slate-800 block uppercase truncate tracking-tight">{c.name}</span>
                                        <span className="text-[10px] text-slate-400">Vol Transaksi: <span className="font-bold text-slate-600 font-mono">{c.qty} pcs</span></span>
                                      </div>
                                    </div>
                                    <div className="text-right font-mono shrink-0">
                                      <span className="font-sans font-bold text-indigo-600 block text-[10px] bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded-md">{percentage}% Share</span>
                                      <span className="text-[10px] text-emerald-600 font-bold block mt-1">Rp{c.revenue.toLocaleString('id-ID')}</span>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* DEFAULT SIDEBAR PLACEHOLDER */}
            {!selectedProduct && (
              <div className="hidden md:flex md:w-[55%] h-full bg-white border-l border-slate-200 flex-col items-center justify-center text-center p-6 text-slate-400">
                <div className="p-4 bg-slate-50 rounded-full border border-slate-100 mb-3 text-slate-300">
                  <TrendingUp className="w-8 h-8" />
                </div>
                <p className="max-w-xs leading-relaxed font-medium text-slate-500">
                  Product Contribution Deep-Dive
                </p>
                <p className="max-w-xs text-[11px] text-slate-400 mt-1">
                  Pilih salah satu produk di panel kiri untuk melihat data ulasan pelanggan atau performa kontribusi kru penjualan.
                </p>
              </div>
            )}

          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}